/**
 * Origin Shield analytics — for one subdomain. Flat tabs:
 *  - Request / Traffic: Owl `GetOriginShield{Request,Traffic}Diagram` — a single
 *    volume series, Summary (line) vs Divide (filled area).
 *  - Firewall Rules / Access Control: WAF mitigation (`WafMitigationPanel`) —
 *    an attack-category donut + metric grid + Top IP/Country chart & table,
 *    scoped to a `module` (`Firewall rules,CRS rules` / `DDoS,Access control`).
 *  - Threat Log: the incident explorer (`WafThreatLogPanel`).
 * Subdomain-scoped; 60m…30d presets + custom datetime range. A zone with no
 * `ever_proxied` subdomain → empty state.
 */
import { useMemo, useState } from 'react';
import { Shield } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useWafOriginShieldDiagram, type WafOriginShieldKind } from '@/api/waf';
import { ChartCard, KpiTiles, RefreshButton, Stack } from '../../_shared/AnalyticsKit';
import { formatBytesSI, formatBytesAxisSI, formatCount, formatNum } from '../../_shared/analytics-format';
import { WafScopeBar, type WafScope } from './WafScopeBar';
import { WafMitigationPanel } from './WafMitigationPanel';
import { WafThreatLogPanel } from './WafThreatLogPanel';
import { WafRangeControl, type ResolvedRange } from './WafRangeControl';
import { intervalForRange, originShieldRows, type ChartMode } from './diagram-transform';
import { loadJSON, saveJSON } from './storage';
import styles from './WafAnalytics.module.css';

const sum = (xs: number[] | undefined) => (xs ?? []).reduce((a, b) => a + (Number(b) || 0), 0);

/** Fixed category labels per mitigation tab (matched against summary.labels). */
const FIREWALL_CATEGORIES = ['Firewall rules', 'API protection', 'XSS rules', 'SQL injection', 'Generic injection'];
const ACCESS_CATEGORIES = ['Blacklist', 'SEO', 'Scanner', 'Bad bot', 'Browser integrity', 'Bot management'];

/** The flat tab set — volume views, two mitigation views, and the threat log. */
type ShieldTab = WafOriginShieldKind | 'firewall-rules' | 'access-control' | 'threat-log';

export function WafOriginShieldAnalytics({ serviceId }: { serviceId: string }) {
  const tabKey = `waf:${serviceId}:origin-shield:tab`;
  const modeKey = `waf:${serviceId}:origin-shield:mode`;

  const [scope, setScope] = useState<WafScope>({});
  const [range, setRange] = useState<ResolvedRange | null>(null);
  const [tab, setTab] = useState<ShieldTab>(() => loadJSON<ShieldTab>(tabKey, 'request'));
  const [mode, setMode] = useState<ChartMode>(() => loadJSON<ChartMode>(modeKey, 'summary'));

  const domain = scope.subdomain;
  // A zone is selected but has no proxied subdomain → nothing to chart.
  const noSubdomain = !!scope.zone && !domain;
  const isFirewall = tab === 'firewall-rules';
  const isAccessControl = tab === 'access-control';
  const isThreatLog = tab === 'threat-log';
  const isOriginTab = tab === 'request' || tab === 'traffic';
  const isTraffic = tab === 'traffic';
  const interval = range ? intervalForRange(range.start_time, range.end_time) : 86400;
  const hasScope = !!domain && !!range;

  // ── Origin Shield (Request / Traffic tabs) ──────────────────────────────
  const osKind: WafOriginShieldKind = tab === 'traffic' ? 'traffic' : 'request';
  const q = useWafOriginShieldDiagram(
    serviceId,
    osKind,
    {
      domain: domain ?? '',
      start_time: range?.start_time ?? 0,
      end_time: range?.end_time ?? 0,
      interval,
    },
    hasScope && isOriginTab,
  );
  const { rows: osRows, series: osSeries } = useMemo(
    () => originShieldRows(q.data, interval, isTraffic ? 'Traffic' : 'Requests'),
    [q.data, interval, isTraffic],
  );
  // Active-tab total (no total in the response → sum the series).
  const osTotal = useMemo(() => sum(q.data?.data), [q.data]);

  const changeTab = (t: ShieldTab) => {
    setTab(t);
    saveJSON(tabKey, t);
  };
  const changeMode = (m: ChartMode) => {
    setMode(m);
    saveJSON(modeKey, m);
  };

  return (
    <Container size="xl">
      <PageHeader
        icon={<Shield size={22} />}
        title="Origin Shield"
        description="Origin Shield volume and WAF mitigation for the selected subdomain."
      />

      <div className={styles.toolbar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
        <div className={styles.controls}>
          <WafRangeControl serviceId={serviceId} pageKey="origin-shield" onChange={setRange} />
          {isOriginTab && (
            <div className={styles.controlsRight}>
              <Select value={mode} onValueChange={(v) => changeMode(v as ChartMode)}>
                <SelectTrigger aria-label="Chart mode" className={styles.modeSelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">Summary</SelectItem>
                  <SelectItem value="divide">Divide</SelectItem>
                </SelectContent>
              </Select>
              <RefreshButton onRefresh={() => q.refetch()} />
            </div>
          )}
        </div>
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<Shield size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to monitor. Proxy a record to see Origin Shield analytics."
        />
      ) : (
        <Stack>
          <Tabs value={tab} onValueChange={(v) => changeTab(v as ShieldTab)}>
            <TabsList>
              <TabsTrigger value="request">Request</TabsTrigger>
              <TabsTrigger value="traffic">Traffic</TabsTrigger>
              <TabsTrigger value="firewall-rules">Firewall Rules</TabsTrigger>
              <TabsTrigger value="access-control">Access Control / Bot management</TabsTrigger>
              <TabsTrigger value="threat-log">Threat Log</TabsTrigger>
            </TabsList>
          </Tabs>

          {isThreatLog ? (
            domain && range ? (
              <WafThreatLogPanel serviceId={serviceId} domain={domain} range={range} />
            ) : null
          ) : isFirewall ? (
            domain && range ? (
              <WafMitigationPanel
                serviceId={serviceId}
                domain={domain}
                range={range}
                module="Firewall rules,CRS rules"
                categories={FIREWALL_CATEGORIES}
                storageKey="firewall"
              />
            ) : null
          ) : isAccessControl ? (
            domain && range ? (
              <WafMitigationPanel
                serviceId={serviceId}
                domain={domain}
                range={range}
                module="DDoS,Access control"
                categories={ACCESS_CATEGORIES}
                storageKey="access-control"
              />
            ) : null
          ) : (
            <>
              <KpiTiles
                columns={1}
                loading={q.isLoading}
                items={[
                  isTraffic
                    ? { label: 'Total bytes', value: formatBytesSI(osTotal) }
                    : { label: 'Total requests', value: formatNum(osTotal) },
                ]}
              />

              <div className={mode === 'divide' ? styles.denseFill : undefined}>
                <ChartCard
                  title={isTraffic ? 'Origin Shield traffic' : 'Origin Shield requests'}
                  description={`${isTraffic ? 'Traffic' : 'Request'} volume over the selected range.`}
                  data={osRows}
                  xKey="t"
                  series={osSeries}
                  type={mode === 'divide' ? 'area' : 'line'}
                  height={280}
                  stacked={mode === 'divide'}
                  showLegend
                  // `key` is set for the tooltip only → full unit there, compact Y-axis.
                  valueFormatter={
                    isTraffic
                      ? (v, key) => (key ? formatBytesSI(Number(v)) : formatBytesAxisSI(Number(v)))
                      : (v) => formatCount(Number(v))
                  }
                  loading={q.isLoading}
                />
              </div>
            </>
          )}
        </Stack>
      )}
    </Container>
  );
}

export default WafOriginShieldAnalytics;
